import { AlertTriangle } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useId, useState } from "react"
import { toast } from "sonner"
import { Button } from "~/components/ui/button"
import { useWorkspaceTab } from "~/components/workspace/Workspace"

export function UnsavedChanges({
	isVisible,
	isDirty = isVisible,
	message = "Careful — you have unsaved changes!",
	protectNavigation = true,
	canSave = true,
	pending = false,
	onRestoreDraft,
	onSave,
	onReset
}: {
	isVisible: boolean
	isDirty?: boolean
	message?: string
	protectNavigation?: boolean
	canSave?: boolean
	pending?: boolean
	onRestoreDraft?: () => Promise<void>
	onSave: () => Promise<void>
	onReset?: () => Promise<void>
}) {
	const [isSaving, setIsSaving] = useState(false)
	const busy = isSaving || pending
	const workspaceTab = useWorkspaceTab()
	const source = useId()
	useEffect(() => {
		workspaceTab?.setDirty(source, protectNavigation && isDirty)
		// Activity suspends effects in hidden tabs; keep their dirty marker until
		// they resume, navigate, or close so unloading still protects their drafts.
	}, [isDirty, protectNavigation, source, workspaceTab])

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (protectNavigation && isDirty) {
				e.preventDefault()
				e.returnValue = "legacy woo"
			}
		}
		window.addEventListener("beforeunload", handleBeforeUnload)
		return () => window.removeEventListener("beforeunload", handleBeforeUnload)
	}, [isDirty, protectNavigation])

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					data-onboarding-support={!protectNavigation || undefined}
					className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
					initial={{ y: "100%" }}
					animate={{
						y: 0,
						transition: {
							type: "spring",
							stiffness: 400,
							damping: 15
						}
					}}
					exit={{
						y: "100%",
						transition: {
							type: "spring",
							stiffness: 300,
							damping: 30
						}
					}}
				>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full max-w-5xl p-3 sm:p-4 mx-2 sm:mx-4 mb-3 sm:mb-4 border rounded-lg shadow-lg pointer-events-auto text-text-main bg-background-tertiary">
						<div className="flex items-center gap-2 text-sm font-medium">
							<AlertTriangle className="w-5 h-5 text-amber-500" />
							<span>{message}</span>
						</div>
						<div className="flex items-center justify-end gap-4">
							{onRestoreDraft ? (
								<Button
									variant="ghost"
									size="sm"
									onClick={async () => {
										setIsSaving(true)
										try {
											await onRestoreDraft()
										} finally {
											setIsSaving(false)
										}
									}}
									disabled={busy}
								>
									Restore draft
								</Button>
							) : null}
							{onReset && (
								<Button
									variant="ghost"
									size="sm"
									onClick={async () => {
										setIsSaving(true)
										try {
											await onReset()
										} finally {
											setIsSaving(false)
										}
									}}
									disabled={busy}
								>
									Reset
								</Button>
							)}
							<Button
								size="sm"
								disabled={!canSave}
								pending={busy}
								onClick={async () => {
									setIsSaving(true)
									try {
										await Promise.all([
											onSave(),
											new Promise((resolve) => setTimeout(resolve, 500))
										])
									} catch (error) {
										toast.error("Failed to save changes:", {
											description: `${error}`
										})
									} finally {
										setIsSaving(false)
									}
								}}
								className="w-36"
							>
								Save Changes
							</Button>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
